<template>
  <div class="cloud-layer" aria-hidden="true">
    <!-- Shared SVG defs: filters and gradients live here once per layer
         instance. IDs are scoped via defId() so multiple layers can coexist. -->
    <svg class="cloud-defs" width="0" height="0" focusable="false">
      <defs>
        <!-- Edge-fluff filters: fractal-noise displacement breaks the smooth
             silhouette into cauliflower-like fluff. Two seed variants give
             each cloud a unique edge pattern. -->
        <filter
          :id="defId('fluff-a')"
          x="-15%" y="-15%" width="130%" height="130%"
          color-interpolation-filters="sRGB"
        >
          <feTurbulence type="fractalNoise" baseFrequency="0.018 0.034" numOctaves="2" seed="3" result="n" />
          <feDisplacementMap in="SourceGraphic" in2="n" scale="6" />
        </filter>
        <filter
          :id="defId('fluff-b')"
          x="-15%" y="-15%" width="130%" height="130%"
          color-interpolation-filters="sRGB"
        >
          <feTurbulence type="fractalNoise" baseFrequency="0.022 0.04" numOctaves="2" seed="11" result="n" />
          <feDisplacementMap in="SourceGraphic" in2="n" scale="7" />
        </filter>

        <!-- Top-lit gradients: bright crown, lavender/peach underbelly.
             Three variants seed atmospheric variety across the layer. -->
        <linearGradient :id="defId('cool')" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0"    stop-color="#ffffff" stop-opacity="0.97" />
          <stop offset="0.40" stop-color="#f8f5fc" stop-opacity="0.93" />
          <stop offset="0.72" stop-color="#dcd0ea" stop-opacity="0.86" />
          <stop offset="1"    stop-color="#a89cc3" stop-opacity="0.55" />
        </linearGradient>
        <linearGradient :id="defId('warm')" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0"    stop-color="#fffefa" stop-opacity="0.97" />
          <stop offset="0.40" stop-color="#fef4e2" stop-opacity="0.92" />
          <stop offset="0.72" stop-color="#e8d0c3" stop-opacity="0.84" />
          <stop offset="1"    stop-color="#c8a89e" stop-opacity="0.50" />
        </linearGradient>
        <linearGradient :id="defId('neutral')" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0"    stop-color="#fefcfd" stop-opacity="0.96" />
          <stop offset="0.50" stop-color="#eee2f0" stop-opacity="0.87" />
          <stop offset="1"    stop-color="#beaed2" stop-opacity="0.50" />
        </linearGradient>

        <!-- Inner highlight: bright sun-kissed edge near the cloud crown.
             Radial so it only catches the upper bumps. -->
        <radialGradient :id="defId('crown')" cx="0.5" cy="0.18" r="0.55">
          <stop offset="0"   stop-color="#ffffff" stop-opacity="0.55" />
          <stop offset="0.6" stop-color="#ffffff" stop-opacity="0.10" />
          <stop offset="1"   stop-color="#ffffff" stop-opacity="0" />
        </radialGradient>
      </defs>
    </svg>

    <!-- Cloud 1 — wide cumulus, 5 humps, cool light -->
    <svg class="cloud cloud--1" viewBox="0 0 200 100" focusable="false">
      <g :filter="`url(#${defId('fluff-a')})`">
        <path
          d="M18 80 C8 78,4 64,14 56 C12 38,30 30,44 40 C46 22,70 14,90 28 C100 12,132 12,148 26 C156 14,180 18,188 36 C196 38,198 64,188 78 C156 82,124 76,92 80 C60 82,36 80,18 80 Z"
          :fill="`url(#${defId('cool')})`"
        />
        <path
          d="M18 80 C8 78,4 64,14 56 C12 38,30 30,44 40 C46 22,70 14,90 28 C100 12,132 12,148 26 C156 14,180 18,188 36 C196 38,198 64,188 78 C156 82,124 76,92 80 C60 82,36 80,18 80 Z"
          :fill="`url(#${defId('crown')})`"
        />
      </g>
    </svg>

    <!-- Cloud 2 — compact storm-puff, 3 chunky humps, warm light -->
    <svg class="cloud cloud--2" viewBox="0 0 200 100" focusable="false">
      <g :filter="`url(#${defId('fluff-b')})`">
        <path
          d="M16 80 C4 78,4 60,16 54 C18 32,50 24,70 38 C78 16,122 12,140 32 C158 32,168 60,156 76 C124 80,92 76,60 78 C38 80,24 80,16 80 Z"
          :fill="`url(#${defId('warm')})`"
        />
        <path
          d="M16 80 C4 78,4 60,16 54 C18 32,50 24,70 38 C78 16,122 12,140 32 C158 32,168 60,156 76 C124 80,92 76,60 78 C38 80,24 80,16 80 Z"
          :fill="`url(#${defId('crown')})`"
        />
      </g>
    </svg>

    <!-- Cloud 3 — long, low stratiform-ish, 6 small humps, neutral -->
    <svg class="cloud cloud--3" viewBox="0 0 200 100" focusable="false">
      <g :filter="`url(#${defId('fluff-a')})`">
        <path
          d="M12 75 C4 73,4 60,14 56 C16 42,32 38,44 46 C48 32,70 30,84 40 C90 28,110 26,124 38 C132 28,154 30,164 42 C172 34,188 38,192 52 C196 60,192 72,184 75 C152 78,116 73,80 76 C50 78,26 75,12 75 Z"
          :fill="`url(#${defId('neutral')})`"
        />
        <path
          d="M12 75 C4 73,4 60,14 56 C16 42,32 38,44 46 C48 32,70 30,84 40 C90 28,110 26,124 38 C132 28,154 30,164 42 C172 34,188 38,192 52 C196 60,192 72,184 75 C152 78,116 73,80 76 C50 78,26 75,12 75 Z"
          :fill="`url(#${defId('crown')})`"
        />
      </g>
    </svg>

    <!-- Cloud 4 — small detached puff, 2-3 humps, cool -->
    <svg class="cloud cloud--4" viewBox="0 0 200 100" focusable="false">
      <g :filter="`url(#${defId('fluff-b')})`">
        <path
          d="M18 80 C6 78,6 56,20 50 C22 30,60 22,78 40 C90 18,130 22,144 42 C162 44,168 70,156 80 C124 84,90 80,56 80 C36 80,24 80,18 80 Z"
          :fill="`url(#${defId('cool')})`"
        />
        <path
          d="M18 80 C6 78,6 56,20 50 C22 30,60 22,78 40 C90 18,130 22,144 42 C162 44,168 70,156 80 C124 84,90 80,56 80 C36 80,24 80,18 80 Z"
          :fill="`url(#${defId('crown')})`"
        />
      </g>
    </svg>

    <!-- Cloud 5 — asymmetric drifter, 4 humps right-leaning, warm -->
    <svg class="cloud cloud--5" viewBox="0 0 200 100" focusable="false">
      <g :filter="`url(#${defId('fluff-a')})`">
        <path
          d="M14 82 C4 80,4 66,16 60 C18 44,36 38,50 48 C54 32,78 28,94 40 C102 20,138 14,158 32 C168 16,194 22,194 50 C196 60,190 76,182 82 C150 86,114 80,78 82 C48 84,26 82,14 82 Z"
          :fill="`url(#${defId('warm')})`"
        />
        <path
          d="M14 82 C4 80,4 66,16 60 C18 44,36 38,50 48 C54 32,78 28,94 40 C102 20,138 14,158 32 C168 16,194 22,194 50 C196 60,190 76,182 82 C150 86,114 80,78 82 C48 84,26 82,14 82 Z"
          :fill="`url(#${defId('crown')})`"
        />
      </g>
    </svg>

    <!-- Cloud 6 — wispy mid-sky, 3 humps with tail, neutral -->
    <svg class="cloud cloud--6" viewBox="0 0 200 100" focusable="false">
      <g :filter="`url(#${defId('fluff-b')})`">
        <path
          d="M10 72 C2 70,2 56,12 52 C14 38,32 32,48 42 C56 26,86 22,102 38 C116 30,142 36,152 50 C168 50,176 66,166 74 C134 78,102 72,70 75 C44 76,22 74,10 72 Z"
          :fill="`url(#${defId('neutral')})`"
        />
        <path
          d="M10 72 C2 70,2 56,12 52 C14 38,32 32,48 42 C56 26,86 22,102 38 C116 30,142 36,152 50 C168 50,176 66,166 74 C134 78,102 72,70 75 C44 76,22 74,10 72 Z"
          :fill="`url(#${defId('crown')})`"
        />
      </g>
    </svg>
  </div>
</template>

<script>
/**
 * CloudLayer — 6 atmospheric SVG clouds drifting downward perpetually.
 *
 * Mounted once at the signup-shell layer (Entry / PIN / wizard frame).
 * Renders into a `position: fixed` layer at z-index 0; the consumer's
 * content should sit above it (use `.signup-shell-content` or any
 * z-index >= 1).
 *
 * Realism approach (vs. the prior cartoon bubble shapes):
 *   1. Irregular cumulus silhouettes — flat-ish base, cauliflower top
 *      with multiple humps of varying heights. Six unique paths give
 *      each cloud its own personality.
 *   2. Top-lit linear gradients — bright crown fading through pale
 *      lavender/peach to a deeper shadowed underbelly. Three gradient
 *      variants (cool / warm / neutral) match the sky's lavender-to-
 *      gold horizon and add atmospheric variety.
 *   3. Radial crown highlight — a soft white sun-kissed glow biased to
 *      the top of each cloud, simulating direct sunlight on the upper
 *      bumps.
 *   4. Turbulence displacement filter — a static fractal-noise filter
 *      breaks the smooth silhouette into fluffy cauliflower edges. Two
 *      seed variants alternate across the six clouds. The filter is
 *      applied once at rasterization, not per-frame, so animation cost
 *      is unchanged (still pure transform compositing).
 *   5. CSS drop-shadow — a soft purple cast shadow on the .cloud class
 *      gives the layer depth against the sky background. Because CSS
 *      drop-shadow follows the displaced silhouette, the cast shadow
 *      naturally adopts the fluffy edge shape.
 *
 * Defs (filters + gradients) live in a single hidden 0×0 SVG at the top
 * of the layer; cloud SVGs reference them by `url(#…)`. IDs are scoped
 * per-instance via defId() so multiple shells on the same page (defensive
 * — should not happen) don't collide.
 *
 * Acceleration: the wizard toggles `body.is-rising` on step change to
 * briefly speed clouds up. Keyframes / overrides are defined below.
 *
 * Reduced motion: clouds freeze via `prefers-reduced-motion`. The static
 * silhouettes and gradients still read as realistic clouds.
 */
let nextCloudLayerId = 0

export default {
  name: 'CloudLayer',
  data() {
    return {
      uid: ++nextCloudLayerId,
    }
  },
  methods: {
    defId(slug) {
      return `${slug}-cl${this.uid}`
    },
  },
}
</script>

<style lang="scss">
/* =====================================================================
   Animated cloud layer — gentle downward drift gives a rising sensation
   as the user moves through steps. On step change, clouds accelerate
   briefly to amplify the "ascending higher" feel. Reduced-motion safe.

   These rules MUST be global (unscoped) — `body.is-rising` is set on
   <body>, and the keyframes / class names need to apply across shells.
   ===================================================================== */
.cloud-layer {
  position: fixed;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
  z-index: 0;
}
/* Hidden defs SVG — must not affect layout. */
.cloud-defs {
  position: absolute;
  width: 0;
  height: 0;
  pointer-events: none;
}
.cloud {
  position: absolute;
  top: 0;
  /* Soft purple cast shadow — follows the fluffy displaced silhouette,
     so the shadow itself looks organic. */
  filter: drop-shadow(0 10px 22px rgba(85, 60, 130, 0.16));
  animation-name: cloud-drift;
  animation-timing-function: linear;
  animation-iteration-count: infinite;
  will-change: transform;
}
.cloud--1 { left: 4%;   width: 220px; opacity: 0.92; animation-duration: 95s;  animation-delay: -10s; }
.cloud--2 { left: 70%;  width: 150px; opacity: 0.84; animation-duration: 120s; animation-delay: -38s; transform: scaleX(-1); }
.cloud--3 { left: 32%;  width: 280px; opacity: 0.70; animation-duration: 150s; animation-delay: -65s; }
.cloud--4 { left: 84%;  width: 110px; opacity: 0.88; animation-duration: 78s;  animation-delay: -22s; transform: scaleX(-1); }
.cloud--5 { left: 16%;  width: 190px; opacity: 0.78; animation-duration: 110s; animation-delay: -52s; transform: scaleX(-1); }
.cloud--6 { left: 56%;  width: 130px; opacity: 0.82; animation-duration: 88s;  animation-delay: -5s;  }

@keyframes cloud-drift {
  from { transform: translateY(-32vh); }
  to   { transform: translateY(130vh); }
}
/* Mirrored clouds use a different keyframe so the scaleX(-1) is preserved */
.cloud--2, .cloud--4, .cloud--5 { animation-name: cloud-drift-mirrored; }
@keyframes cloud-drift-mirrored {
  from { transform: scaleX(-1) translateY(-32vh); }
  to   { transform: scaleX(-1) translateY(130vh); }
}

/* On step change: clouds accelerate for ~1.4s, simulating an ascent. */
body.is-rising .cloud {
  animation-duration: 4.5s !important;
  animation-timing-function: cubic-bezier(.4, .65, .3, 1);
}

@media (prefers-reduced-motion: reduce) {
  .cloud { animation: none; }
}
</style>
